import cx from "classnames";
import React from "react";

import { Comment } from "@/mk2/apps/blogs/components/Comment";
import { CommentsDesign } from "@/mk2/apps/blogs/constants";
import { AdSlot } from "@/mk2/containers/AdSlot/AdSlot";
import Complaint from "@/mk2/containers/Complaint/Complaint";
import { ComplaintTargetType } from "@/mk2/containers/Complaint/Complaint.schemas";
import Deletion from "@/mk2/containers/Deletion/Deletion";
import { DeletionTargetType } from "@/mk2/containers/Deletion/Deletion.schemas";
import { BlogPostEntity, CommentEntity, UserEntity } from "@/mk2/schemas";
import { HistoryLocationWithState, RouteNavigateFunction } from "@/mk2/services/browserHistory";

import styles from "./CommentList.mscss";

interface OwnProps {
    comments: CommentEntity[];
    commentsCount: number; // number of visible comments for requestUser
    parentPost: BlogPostEntity;
    requestUser: UserEntity;
    requestUserPermissions: string[];
    isMobile: boolean;
    moderators: UserEntity[];
    hasHiddenText: boolean;
    highlightTriggeredId: number;
    highlightStartedId: number;
    design: CommentsDesign;
    complaintsOpened: boolean[];
    deletionsOpened: boolean[];
    blockedUserIds?: number[];
    location: HistoryLocationWithState;
    routeNavigate: RouteNavigateFunction;

    onDidHighlight?(commentId: number);
    onReply(username: string, repliedToCommentId: number);
    onDelete(commentId: number);
    onReport(
        entityType: ComplaintTargetType,
        commentId: number,
        location: HistoryLocationWithState,
    );
    onSendHeart();
    onToggleMenuMore?(commentId: number);
}

export function getAdPositions(commentCount: number, groupSize = 10): number[] {
    if (commentCount < groupSize) return [];

    const adCount = Math.floor(commentCount / groupSize);
    const positions: number[] = [];
    const step = commentCount / (adCount + 1);

    for (let i = 1; i <= adCount; i++) {
        let position = Math.round(i * step);
        if (position === 0) position = 1;
        if (position >= commentCount) position = commentCount - 1;
        positions.push(position);
    }

    return positions;
}

export class CommentList extends React.PureComponent<OwnProps> {
    public render() {
        const {
            comments,
            commentsCount,
            requestUser,
            requestUserPermissions,
            design,
            highlightStartedId,
            highlightTriggeredId,
            isMobile,
            complaintsOpened,
            location,
            routeNavigate,
            moderators,
            parentPost,
            hasHiddenText,
            deletionsOpened,
            blockedUserIds,
            onDelete,
            onReport,
            onDidHighlight,
            onSendHeart,
            onToggleMenuMore,
            onReply,
        } = this.props;

        const adPositions = getAdPositions(commentsCount);
        const inGroups = parentPost && "group" in parentPost && !!parentPost.group;
        const adSlotZone =
            parentPost &&
            (inGroups ? "Groups Post - Mid 01 - Mobile" : "Blogs Article - Mid 02 - Mobile");
        return (
            <div
                className={cx(styles.CommentList, styles[`CommentList--${CommentsDesign[design]}`])}
            >
                {comments.map((comment, index) => (
                    <div
                        key={comment.id}
                        className={cx(
                            styles.CommentList__commentWrap,
                            complaintsOpened[index] &&
                                styles["CommentList__commentWrap--reporting"],
                            deletionsOpened[index] && styles["CommentList__commentWrap--reporting"],
                        )}
                    >
                        <Complaint
                            className={cx(
                                styles.CommentList__complaint,
                                styles[`CommentList__complaint--${CommentsDesign[design]}`],
                            )}
                            entity={ComplaintTargetType.PhotoblogComment}
                            id={comment.id}
                            inForum={false}
                            inGroups={inGroups}
                        />

                        <Deletion
                            className={cx(
                                styles.CommentList__deletion,
                                styles[`CommentList__deletion--${CommentsDesign[design]}`],
                            )}
                            entity={DeletionTargetType.PhotoblogComment}
                            id={comment.id}
                        />

                        <Comment
                            key={comment.id}
                            inForum={false}
                            inGroups={design === CommentsDesign.Normal}
                            comment={comment}
                            followLinks={false}
                            highlightTriggered={comment.id === highlightTriggeredId}
                            highlightStarted={comment.id === highlightStartedId}
                            isMobile={isMobile}
                            requestUser={requestUser}
                            requestUserPermissions={requestUserPermissions}
                            hasBodyHiddenText={hasHiddenText}
                            hasBodyDeletedStatus={false}
                            hasFooterLike
                            hasFooterReply
                            hasMenu
                            hasMenuReport={design === CommentsDesign.Normal}
                            hasMenuEdit
                            hasMenuDelete={design === CommentsDesign.Normal}
                            hasMenuMoveUp={false}
                            blockedUserIds={blockedUserIds}
                            moderators={moderators}
                            onDidHighlight={onDidHighlight}
                            onReply={onReply}
                            onDelete={onDelete}
                            onReport={this.onReport}
                            onSendHeart={onSendHeart}
                            onToggleMenuMore={onToggleMenuMore}
                            location={location}
                            routeNavigate={routeNavigate}
                            post={parentPost}
                        />
                        {adPositions.includes(index + 1) && (
                            <AdSlot
                                key={`ad-${index}`}
                                className={styles.ProductDetail__rectangleAd}
                                zone={adSlotZone}
                                location={location}
                                marginTopWithAd={20}
                            />
                        )}
                    </div>
                ))}
            </div>
        );
    }

    private onReport = (entityType: ComplaintTargetType, commentId: number) => {
        const { onReport, location } = this.props;
        onReport(entityType, commentId, location);
    };
}
